/**
 * ForgotPasswordPage — Demande de réinitialisation de mot de passe
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../contexts/ThemeContext.js';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';

export function ForgotPasswordPage() {
  const settings = useSettings();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const res = await apiFetch<{ message: string }>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      if (res.success) {
        setSent(true);
      } else {
        setError(res.error ?? 'Une erreur est survenue');
      }
    } catch {
      setError('Impossible de contacter le serveur');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="w-full max-w-md">
        {/* En-tête */}
        <div className="text-center mb-8">
          {settings.logoUrl ? (
            <img
              src={settings.logoUrl}
              alt={settings.siteName}
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : (
            <div className="h-16 w-16 mx-auto mb-4 rounded-xl bg-primary flex items-center justify-center">
              <span className="text-2xl font-bold text-accent">A</span>
            </div>
          )}
          <h1 className="text-2xl font-heading font-bold text-accent">
            {settings.siteName}
          </h1>
        </div>

        <div className="bg-surface rounded-xl shadow-lg p-6 border border-gray-100">
          {sent ? (
            /* Confirmation */
            <div className="text-center space-y-4">
              <div
                className="h-12 w-12 mx-auto rounded-full flex items-center justify-center"
                style={{ backgroundColor: colors.accentLight }}
              >
                <i className="bi bi-envelope-check" style={{ fontSize: 24, color: colors.accent }} />
              </div>
              <h2 className="text-lg font-semibold text-accent">Demande envoyée</h2>
              <p className="text-sm text-text-light">
                Si un compte existe avec l'adresse <strong className="text-text">{email}</strong>,
                un lien de réinitialisation a été généré.
              </p>
              <p className="text-xs text-text-light">
                Vérifiez les logs de l'API pour récupérer le lien.
              </p>
              <Link
                to="/login"
                className="inline-block mt-4 text-sm font-medium hover:underline"
                style={{ color: colors.accent }}
              >
                Retour à la connexion
              </Link>
            </div>
          ) : (
            /* Formulaire */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="text-center mb-2">
                <i className="bi bi-key" style={{ fontSize: 32, color: colors.accent, display: 'block', marginBottom: 8 }} />
                <h2 className="text-lg font-semibold text-accent">Mot de passe oublié</h2>
                <p className="text-sm text-text-light mt-1">
                  Entrez votre adresse email pour recevoir un lien de réinitialisation
                </p>
              </div>

              {error && (
                <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text mb-1">
                  Email
                </label>
                <div className="relative">
                  <i className="bi bi-envelope absolute left-3 top-1/2 -translate-y-1/2" style={{ fontSize: 14, color: colors.textMuted }} />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    autoFocus
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300
                               text-text placeholder:text-text-light
                               focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 rounded-lg bg-primary text-accent font-semibold
                           hover:bg-primary-light transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Envoi...' : 'Réinitialiser le mot de passe'}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm hover:underline"
                  style={{ color: colors.accent }}
                >
                  Retour à la connexion
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
